'use client';

import { useState } from 'react';
import Link from 'next/link';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import PenIcon from '@/components/icons/pen';
import { socialLinks } from '@/data/social-links';
import InfoBlueIcon from '@/components/icons/info-blue';
import { useSaleSetupForm } from '@/features/original-sale';
import InfoIcon from '@/components/icons/info';
import { SaleConfirmationModal } from './sale-confirmation-modal';

interface SaleSetupFormProps {
  postId?: string;
  initialPrice?: number;
  initialFreeChaptersCount?: number;
  sellerRewardPercent?: number;
  applicationStatus?: string;
  onSuccess?: () => void;
}

export const SaleSetupForm = ({
  postId,
  initialPrice,
  initialFreeChaptersCount,
  sellerRewardPercent = 70,
  applicationStatus,
  onSuccess,
}: SaleSetupFormProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const {
    form,
    onSubmit,
    onSubmitDirect,
    isSubmitting,
    isValid,
    isEditing,
    toggleEditMode,
    hasNewExistingApplication,
    hasCompletedApplication,
  } = useSaleSetupForm({
    postId,
    initialPrice,
    initialFreeChaptersCount,
    applicationStatus,
    onSuccess,
  });

  const isFormDisabled = (hasNewExistingApplication || hasCompletedApplication) && !isEditing;
  const shouldShowSubmitButton =
    (!hasNewExistingApplication && !hasCompletedApplication && !isEditing) ||
    ((hasNewExistingApplication || hasCompletedApplication) && isEditing);
  const shouldShowEditButton = (hasNewExistingApplication || hasCompletedApplication) && !isEditing;
  const isStartingSale = !hasNewExistingApplication && !isEditing;

  const handleIncrement = () => {
    const currentValue = form.getValues('freeChaptersCount');
    form.setValue('freeChaptersCount', currentValue + 1, { shouldValidate: true });
  };

  const handleDecrement = () => {
    const currentValue = form.getValues('freeChaptersCount');
    form.setValue('freeChaptersCount', Math.max(1, currentValue - 1), { shouldValidate: true });
  };

  const handleChangeNumericInput = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const val = evt.target.value;

    if (val === '') {
      form.setValue('price', '' as unknown as number, {
        shouldDirty: true,
        shouldValidate: false,
      });
      form.clearErrors('price');
      return;
    }

    if (!/^\d+$/.test(val)) {
      return;
    }

    form.setValue('price', Number(val), { shouldDirty: true, shouldValidate: true });
  };

  const handleEditClick = () => {
    toggleEditMode();
  };

  const handleFormSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();

    // If starting sale (not editing), show confirmation modal
    if (isStartingSale) {
      setIsConfirmationOpen(true);
    } else {
      // For editing, submit directly
      onSubmit(evt);
    }
  };

  const handleConfirmSale = async () => {
    setIsConfirmationOpen(false);
    // Get form values and call onSubmitDirect
    const formValues = form.getValues();
    await onSubmitDirect(formValues);
  };

  const handleCloseModal = () => {
    setIsConfirmationOpen(false);
  };

  return (
    <div className="bg-white rounded-xl px-6 py-8">
      <div className="flex flex-col items-center gap-6">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-[#E3E5ED]">
          <PenIcon className="h-6 w-6 text-[#6E7384]" />
        </div>
        <h2 className="text-xl font-semibold text-[#1B1D22] ">Продажа Ориджиналов</h2>
        {applicationStatus === 'new' && (
          <div className="flex gap-2">
            <InfoIcon className="flex-shrink-0 h-5 w-5 text-yellow-500" />
            <p className="text-sm text-yellow-500">
              Отправили заявку и произведение на модерацию. Как правило, это занимает не больше 1-2
              рабочих дней, после чего произведение автоматически начнет продаваться на платформе.
              Спасибо!
            </p>
          </div>
        )}
      </div>

      <h3 className="mt-6 mb-3 text-lg font-semibold text-[#1B1D22]">
        Полная стоимость произведения
      </h3>
      <p className="text-sm text-[#6C7286] ">
        Вы можете продавать ориджиналы со статусом написания "В процессе". Подобные произведения
        необходимо финализировать в течение <span className="font-bold">180 дней</span> с момента
        старта продаж перевести в статус написания "Завершено".
      </p>

      <Form {...form}>
        <form onSubmit={handleFormSubmit} className="mt-3 space-y-6">
          <FormField
            control={form.control}
            name="price"
            render={({ field }) => (
              <FormItem className="space-y-2.5">
                <FormLabel className="text-sm font-normal text-[#888C91]">
                  Стоимость произведения
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Введите стоимость"
                    disabled={isFormDisabled}
                    className="px-[9px] h-10 max-w-[150px] font-medium placeholder:text-[#C0C3D3]"
                    {...field}
                    value={field.value ?? ''}
                    onChange={handleChangeNumericInput}
                    onBlur={(evt) => {
                      field.onBlur();
                      if (evt.target.value === '') {
                        void form.trigger('price');
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-2">
            <h3 className="mb-3 text-lg font-semibold text-[#1B1D22]">
              Количество глав для бесплатного ознакомления
            </h3>
            <p className="text-sm text-[#6C7286]">
              Бесплатные главы снижают сомнения перед покупкой и помогают читателю втянуться в
              историю. Оптимально давать столько, чтобы сработала завязка и появился интерес к
              продолжению обычно это первые <span className="font-bold">5-15% текста.</span>
            </p>

            <FormField
              control={form.control}
              name="freeChaptersCount"
              render={({ field }) => (
                <FormItem className="mt-3">
                  <FormLabel className="text-sm font-normal text-[#888C91]">Кол-во глав</FormLabel>

                  <div className="p-1 flex items-center mt-2 bg-[#F2F3F7] max-w-[132px] rounded-[10px]">
                    <button
                      type="button"
                      onClick={handleDecrement}
                      disabled={isFormDisabled}
                      className="flex h-8 w-8 text-xl items-center justify-center rounded-lg bg-[#25282E] text-white hover:bg-[#25282E]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      -
                    </button>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="1"
                        min="1"
                        step="1"
                        disabled={isFormDisabled}
                        className="h-8 w-[60px] text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none rounded-none placeholder:text-[#C0C3D3]"
                        {...field}
                        value={field.value}
                        onChange={(evt) => {
                          const val = parseInt(evt.target.value) || 1;
                          field.onChange(Math.max(1, val));
                        }}
                      />
                    </FormControl>
                    <button
                      type="button"
                      onClick={handleIncrement}
                      disabled={isFormDisabled}
                      className="flex h-8 w-8 text-xl items-center justify-center rounded-lg bg-[#25282E] text-white hover:bg-[#25282E]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      +
                    </button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-3">
            <h3 className="text-base font-semibold text-[#1B1D22] ">Размер вознаграждения</h3>

            <div className="space-y-2.5">
              <p className="text-sm text-[#6C7286] ">
                Текущий размер вознаграждения{' '}
                <span className="font-bold">{sellerRewardPercent}%.</span>
              </p>
              <p className="text-sm text-[#6C7286]">
                Для получения лучших условий, пожалуйста, свяжитесь со своим личным менеджером, либо
                напишите нам в{' '}
                <Link
                  href={socialLinks.vk}
                  target="_blank"
                  className="underline text-[#505CFF] hover:opacity-80"
                >
                  ВК
                </Link>{' '}
                или{' '}
                <Link
                  href={socialLinks.telegram}
                  target="_blank"
                  className="underline text-[#505CFF] hover:opacity-80"
                >
                  TG.
                </Link>
              </p>
            </div>
          </div>

          {shouldShowSubmitButton && (
            <div className="flex justify-center">
              <Button
                type="submit"
                variant="purple"
                disabled={isSubmitting || !isValid}
                className="h-10 rounded-xl bg-[#505CFF] hover:bg-[#505CFF]/90 text-white font-medium"
              >
                {isSubmitting
                  ? 'Отправка...'
                  : (hasNewExistingApplication || hasCompletedApplication) && isEditing
                    ? 'Сохранить изменения'
                    : 'Начать продажи'}
              </Button>
            </div>
          )}
        </form>
      </Form>

      {shouldShowEditButton && (
        <div className="mt-6 flex justify-center">
          <Button
            type="button"
            onClick={handleEditClick}
            variant="purple"
            className="h-10 rounded-xl bg-[#505CFF] hover:bg-[#505CFF]/90 text-white font-medium"
          >
            Редактировать заявку
          </Button>
        </div>
      )}

      <div className="mt-6 p-4 flex gap-2 rounded-xl bg-[#505CFF14]">
        <InfoBlueIcon className="flex-shrink-0 h-5 w-5" />
        <p className="text-sm text-[#575E74] ">
          Мы уже работаем над функционалом, который позволит вам продавать произведения не только
          целиком, но и по отдельным главам. Спасибо за выбор{' '}
          <span className="font-semibold">Molwe!</span>
        </p>
      </div>

      <SaleConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmSale}
      />
    </div>
  );
};
